import {
  Component,
  ViewEncapsulation,
  SimpleChanges,
  OnChanges,
  Input,
  OnInit,
  HostListener,
} from "@angular/core";
import { Dictionary } from "src/app/store/models/common_types";
import { faChartBar } from "@fortawesome/pro-solid-svg-icons";
import { SurprisePageDataService } from "../surprise-page-data.service";
import { Store } from "@ngrx/store";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import { AppService } from "src/app/app.service";
import { SetDpParams } from "src/app/store/actions/global-subheader.actions";
import { environment } from "src/environments/environment";
import { SetSourceMetaGridFilters } from "src/app/store/actions/right-panel-actions";
import dayjs from "dayjs";
import { VAIconRegistryService, chartBar } from "@va/core-library/icons";

export interface TreeNodeInterface {
  id?: string;
  key: string | number;
  children: Array<any>;
  name: string;
  expand?: boolean;
  level?: number;
  parent?: any;
  view?: string;
  cut?: string;
  shortname?: string;
  dataLoaded?: boolean;
  isTabRow?: boolean;
  isExpandableRow?: boolean;
  source?: string;
}

@Component({
  selector: "app-surprise-grid",
  templateUrl: "./surprise-grid.component.html",
  styleUrls: ["./surprise-grid.component.scss"],
  host: { class: "surprise-grid-component" },
  encapsulation: ViewEncapsulation.None,
})
export class SurpriseGridComponent implements OnChanges, OnInit {
  @Input() surpriseMeta: any;
  @Input() gridFilters: Dictionary<any>;
  faChartBar = faChartBar;
  mapOfExpandData: { [key: string]: boolean } = {};
  listOfData: Array<any>;
  noOfCols: number;
  loading = true;
  gridHeader: Dictionary<any> = {};
  listOfMapData = [];
  periodFireCount = 0;
  paramFireCount = 0;
  gridScrollValue: Dictionary<any> = { x: "0px", y: "0px" };
  showGrid = false;
  highlightBackground = "";
  tabLineCountMapping = {};
  lineItemHeadersExpanded = true;
  mapOfExpandedData: { [key: string]: TreeNodeInterface[] } = {};
  assetsUrl = environment.assetUrl;
  selectedSources = [];
  selectedSourceList = [];
  popOverMapping: any = {};
  isSourceExcluded = {};

  /* Chart variables */
  chartPopupVisible = false;
  chartPopupType = "";
  companyFullName = "";
  chartPopupDataToUse: any = {};
  beginningStateReferrer = "Surprise Analysis Page";
  feature = "SURPRISE_PAGE"; //todo recheck this
  /* Chart variables */

  constructor(
    private dataService: SurprisePageDataService,
    private store: Store<AppState>,
    private appService: AppService,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([chartBar]);
  }

  @HostListener("window:resize", ["$event"])
  onResize(event) {
    this.gridScrollValue.y = `${event.target.innerHeight - 386}px`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.surpriseMeta &&
      !changes.surpriseMeta.firstChange &&
      changes.surpriseMeta.currentValue !== changes.surpriseMeta.previousValue
    ) {
      this.loading = true;
      this.bindSurpriseHeader();
      this.bindSurpriseData();

      this.gridScrollValue = {
        y: `${window.innerHeight - 386}px`, //todo set insisde window resize hook
        x: `${changes.surpriseMeta.currentValue.periods.length * 270 + 380}px`,
      };
      this.showGrid = true;
    }

    if (
      changes.gridFilters &&
      !changes.gridFilters.firstChange &&
      changes.gridFilters.currentValue !== changes.gridFilters.previousValue
    ) {
      this.gridFilters = changes.gridFilters.currentValue;
      this.bindSurpriseHeader();
    }
  }

  ngOnInit() {
    this.store
      .select(stateGetter("rightPanel", "sourceMeta", "selectedSources"))
      .subscribe((selectedSources) => {
        this.isSourceExcluded = {};
        this.selectedSources = selectedSources || [];
        this.selectedSourceList = this.selectedSources.map((source) => {
          this.isSourceExcluded[source.name] = source.excluded;
          return source.name;
        });

        const focusedSource = this.selectedSources.find(
          (source) => source.focus,
        );
        const currFocus = focusedSource ? focusedSource.name : "";
        if (this.selectedSourceList.includes(currFocus)) {
          this.selectedSourceList = this.selectedSourceList.filter(
            (sname) => sname !== currFocus,
          );
          this.selectedSourceList.push("Consensus");
        }
      });
  }

  bindSurpriseHeader() {
    this.bindGridScroll();
    //header level 1
    this.gridHeader.level1 = [
      {
        label: `All numbers in ${this.surpriseMeta.currency}${this.surpriseMeta.magnitude} unless stated otherwise`,
        rowspan: 2,
        width: this.lineItemHeadersExpanded ? "300px" : "190px",
        borderRightDotted: true,
        nzLeft: "0px",
        align: "left",
      },
      {
        label: "",
        rowspan: 2,
        width: "30px",
        borderRightDotted: true,
        nzLeft: this.lineItemHeadersExpanded ? "300px" : "190px",
        align: "center",
      },
      {
        label: "",
        rowspan: 2,
        width: "110px",
        borderRight: true,
        nzLeft: this.lineItemHeadersExpanded ? "330px" : "220px",
        align: "left",
      },
    ];

    this.gridHeader.level2 = [];

    for (let i = 0; i < this.surpriseMeta.periods.length; i++) {
      this.gridHeader.level1 = this.gridHeader.level1.concat({
        label: this.surpriseMeta.periods[i],
        colspan: this.gridFilters.onlySurprise ? 1 : 3,
        width: "90px",
        borderRight: true,
        align: "center",
      });

      this.gridHeader.level2 = this.gridHeader.level2.concat(
        {
          label: "PreQ",
          width: this.gridFilters.onlySurprise ? "0px" : "90px",
          borderRightDotted: true,
          valueHeader: true,
          align: "center",
          popoverKey: `${this.surpriseMeta.periods[i]}-preq`,
        },
        {
          label: "VA Actuals",
          width: this.gridFilters.onlySurprise ? "0px" : "90px",
          borderRightDotted: true,
          valueHeader: true,
          align: "center",
          popoverKey: `${this.surpriseMeta.periods[i]}-va-actuals`,
        },
        {
          label: "Surprise",
          width: "90px",
          borderRight: true,
          valueHeader: true,
          align: "center",
          popoverKey: "",
        },
      );
    }
  }

  bindGridScroll() {
    const colWidth = this.gridFilters.onlySurprise ? 90 : 270;
    const additional = this.lineItemHeadersExpanded ? 380 : 270;
    if (this.gridScrollValue) {
      const xScrollValue = `${
        this.surpriseMeta.periods.length * colWidth + additional
      }px`;
      this.gridScrollValue = {
        ...this.gridScrollValue,
        x: xScrollValue,
      };
    }
  }

  bindSurpriseData() {
    this.listOfMapData = this.surpriseMeta.tabs;
    this.bindLineitems();
  }

  bindLineitems() {
    for (
      let tabIndex = 0;
      tabIndex < this.surpriseMeta.tabs.length;
      tabIndex++
    ) {
      const tab = this.surpriseMeta.tabs[tabIndex];
      if (this.surpriseMeta.filters.cut_expansions.indexOf(tab.name) !== -1) {
        this.dataService
          .getLineitemsData(tab.shortname, tab.view, this.surpriseMeta.ticker)
          .subscribe(
            (response) => {
              const lineitemData = response;
              this.createGridRows(lineitemData, tab.shortname);
            },
            (err) => {
              console.error(err);
            },
          );
      }
    }
  }

  createGridRows(lineitemData: any, tab: string) {
    this.listOfMapData = this.listOfMapData.map((el, i) => {
      const data = Object.assign({}, el);
      data.key = i + 1;
      data.isTabRow = true;
      data.expand =
        this.gridFilters.cut_expansions.indexOf(el.name) !== -1 ? true : false;

      if (tab === el.shortname) {
        data.children = lineitemData.row_dimensions[0].values.map((obj, j) => {
          const l1Data = Object.assign({}, obj);
          l1Data["key"] = `l1-${j}${1}`;
          l1Data["source"] = obj.header ? null : this.surpriseMeta.focus;
          l1Data["isExpandableRow"] = obj.header ? false : true;
          l1Data["parameter_unit"] =
            obj.parameter_unit !== undefined ? obj.parameter_unit : "";

          if (!obj.header) {
            l1Data["children"] = this.selectedSourceList.map((source, k) => {
              const l2Data = Object.assign({}, {});
              l2Data["key"] = `l1${j}${1}-${k}${1}`;
              l2Data["source"] = source;
              l2Data["childRow"] = true;
              return l2Data;
            });
          }
          return l1Data;
        });

        this.periodFireCount = 0;
        if (data.expand === true) {
          // get data only if expanded
          for (let i = 0; i < this.surpriseMeta.periods.length; i++) {
            const currPeriod = this.surpriseMeta.periods[i];
            this.getPgData(tab, currPeriod, "initial");
          }
          data.dataLoaded = true;
        }
      }
      return data;
    });
    this.listOfMapData.forEach((item) => {
      this.mapOfExpandedData[item.key] = this.convertTreeToList(item);
    });
  }

  getPgData(tab: string, period: string, load?: string) {
    const revision =
      this.surpriseMeta.period_revision_dict[period] || "Current";

    this.dataService
      .getPgData(
        period,
        tab,
        this.surpriseMeta.ticker,
        this.surpriseMeta.focus,
        revision,
      )
      .subscribe((response: any) => {
        this.periodFireCount++;
        const currentData = response.data[0].data;
        const preQData = response.data[1].data;
        const vaActualData = response.data[2].data;
        // const pg = response.data[0].PG;
        const period = response.data[0].period;

        const vaActualRevDate = response.data[2].revision_date;
        const preqRevDate = response.data[1].revision_date;

        this.popOverMapping[`${period}-va-actuals`] = vaActualRevDate;
        this.popOverMapping[`${period}-preq`] = preqRevDate
          ? preqRevDate
          : "NA";
        this.listOfMapData
          .filter((el) => el.shortname === tab)[0]
          .children.map((data) => {
            //set default data
            data[`va-actuals-${period}`] = null;
            data[`surprise-${period}`] = null;
            data[`va-actuals-dpid-${period}`] = null;
            data[`unit-${period}`] = null;
            data[`preq-${period}`] = null;
            data[`preq-dpid-${period}`] = null;
            data["precision"] = null;

            const vaActuals = vaActualData.filter(function (value) {
              return value.param_id.toString() === data.id.toString();
            })[0];

            const currData = currentData.filter(function (value) {
              return value.param_id.toString() === data.id.toString();
            })[0];

            const preqVal = preQData.filter(function (value) {
              return value.param_id.toString() === data.id.toString();
            })[0];

            data.source = data.header ? "" : this.surpriseMeta.focus;
            if (vaActuals) {
              data[`va-actuals-${period}`] = vaActuals.value;
              data[`va-actuals-dpid-${period}`] = vaActuals.id;
              data[`unit-${period}`] = vaActuals.unit;
              data["precision"] = vaActuals.precision;
              if (
                this.surpriseMeta.focus &&
                this.surpriseMeta.focus.toLowerCase() === "consensus"
              ) {
                data[`surprise-${period}`] = vaActuals.surprise;
              }
            }

            if (currData) {
              if (
                this.surpriseMeta.focus &&
                this.surpriseMeta.focus.toLowerCase() !== "consensus"
              ) {
                data[`surprise-${period}`] = currData.surprise;
              }
            }

            if (preqVal) {
              data[`preq-${period}`] = preqVal.value;
              data[`preq-dpid-${period}`] = preqVal.id;
            }

            //resetting expansions
            data.expand = false;
            data.dataLoaded = false;
            return data;
          });

        if (this.periodFireCount === this.surpriseMeta.periods.length) {
          // after all pg calls done
          setTimeout(() => {
            this.listOfMapData.forEach((item) => {
              this.mapOfExpandedData[item.key] = this.convertTreeToList(item);
            });
            this.loading = false;
            this.scrollToLastPeriod();
            if (load === "initial") {
              this.bindPendingTabLines();
            }
            this.onFilterChange();
          }, 100);
        }
      });
  }

  bindPendingTabLines() {
    for (
      let tabIndex = 0;
      tabIndex < this.surpriseMeta.tabs.length;
      tabIndex++
    ) {
      if (
        this.surpriseMeta.filters.cut_expansions.indexOf(
          this.surpriseMeta.tabs[tabIndex].name,
        ) === -1
      ) {
        this.dataService
          .getLineitemsData(
            this.surpriseMeta.tabs[tabIndex].shortname,
            this.surpriseMeta.tabs[0].view,
            this.surpriseMeta.ticker,
          )
          .subscribe((response) => {
            const lineitemData = response;
            this.createGridRows(
              lineitemData,
              this.surpriseMeta.tabs[tabIndex].shortname,
            );
          });
      }
    }
  }

  collapse(
    array: TreeNodeInterface[],
    data: TreeNodeInterface,
    $event: boolean,
    eventIsSynthetic?: boolean, //whether the event is triggered by user action or recusive code below
  ) {
    if (!eventIsSynthetic && (data.isTabRow || data.isExpandableRow)) {
      const extras = { beginning_state_referrer: "Surprise Analysis Page" };
      let actionSource = "";
      const mainSource = "";
      let expansionCut = "";

      if (data.isTabRow) {
        //Shows that it is a tab expansion
        if (data.expand === true) {
          actionSource = "Surprise Tab Line Expand";
        } else {
          actionSource = "Surprise Tab Line Collapse";
        }
        extras["tab"] = data.name;
        expansionCut = data.view;
      } else {
        //Shows that it is a source expansion within a tab
        if (data.expand === true) {
          actionSource = "Surprise Source Line Expand";
        } else {
          actionSource = "Surprise Source Line Collapse";
        }
        extras["source"] = this.surpriseMeta.focus;
        extras["source_list"] = this.selectedSourceList;
        expansionCut = data.parent.view;
      }

      this.appService.logUserAction(
        {},
        this.surpriseMeta.ticker,
        expansionCut,
        actionSource,
        extras,
        mainSource,
        actionSource,
      );
    }

    if (data.isTabRow) {
      if (
        data.expand &&
        this.gridFilters.cut_expansions.indexOf(data.name) === -1
      ) {
        //add to cut expansions
        this.gridFilters.cut_expansions.push(data.name);
      } else {
        //remove from cut expansions
        this.gridFilters.cut_expansions =
          this.gridFilters.cut_expansions.filter(
            (cutName) => cutName !== data.name,
          );
      }
      this.saveUserFilters();
    }

    if (data.isTabRow) {
      this.listOfMapData
        .filter((listdata) => listdata.shortname === data.shortname)
        .map((obj) => {
          obj.expand = data.expand;
          if (data.expand === true) {
            obj.dataLoaded = true;
          }
          return obj;
        });
    } else if (data.isExpandableRow) {
      this.listOfMapData
        .filter((listdata) => listdata.shortname === data.parent.shortname)[0]
        .children.map((obj) => {
          if (data.id === obj.id) {
            obj.expand = data.expand;
            if (obj.expand) {
              obj.dataLoaded = true;
            }
          }
          return obj;
        });
    }

    /* 
    $event = true ---> Expanding the tab row 
    $event = false ---> Collapsing the tab row
    */

    if ($event === false) {
      if (data.children) {
        data.children.forEach((d) => {
          const target = array.find((a) => a.key === d.key)!;
          target.expand = false;
          this.collapse(array, target, false, true);
        });
      } else {
        return;
      }
    } else {
      if (!data.dataLoaded && data.expand === true && data.isTabRow) {
        this.loading = true;
        this.periodFireCount = 0;

        for (let i = 0; i < this.surpriseMeta.periods.length; i++) {
          this.getPgData(data.shortname, this.surpriseMeta.periods[i]);
        }
      }

      if (!data.dataLoaded && data.isExpandableRow && data.expand === true) {
        this.getParamData(data.parent.view, data.parent.shortname, data.id);
      }
    }
  }

  getParamData(cut: string, tab: string, id: string) {
    this.paramFireCount = 0;
    this.loading = true;

    for (let i = 0; i < this.surpriseMeta.periods.length; i++) {
      const period = this.surpriseMeta.periods[i];
      const revision =
        this.surpriseMeta.period_revision_dict[period] || "Current";

      const params = {
        ticker: this.surpriseMeta.ticker,
        cut: cut,
        pg: tab,
        paramId: parseInt(id),
        period: period,
        sources: [...this.selectedSourceList, "VA Actuals"],
        revision: revision,
      };

      this.dataService.getParamData(params).subscribe((response: any) => {
        this.paramFireCount++;

        if (response.data && response.data.length > 0) {
          this.listOfMapData
            .filter((el) => el.shortname === tab)[0]
            .children.filter((data) => data.id === id)
            .map((rowData) => {
              rowData.children.map((sourceData) => {
                const preqData = response.data.filter((data) => {
                  return (
                    data.key.split("|")[0].toLowerCase() ===
                      sourceData.source.toLowerCase() &&
                    data.revision.toLowerCase() === "preq"
                  );
                })[0];

                const vaActualData = response.data.filter((data) => {
                  return (
                    data.key.split("|")[0].toLowerCase() === "va actuals" &&
                    data.revision.toLowerCase() !== "preq"
                  );
                })[0];

                let surpriseData: any = {};
                if (sourceData.source.toLowerCase() === "consensus") {
                  surpriseData = response.data.filter((data) => {
                    return (
                      data.key.split("|")[0].toLowerCase() === "va actuals" &&
                      data.revision.toLowerCase() !== "preq"
                    );
                  })[0];
                } else {
                  surpriseData = response.data.filter((data) => {
                    return (
                      data.key.split("|")[0].toLowerCase() ===
                        sourceData.source.toLowerCase() &&
                      data.revision.toLowerCase() !== "preq"
                    );
                  })[0];
                }

                if (vaActualData && vaActualData.data.length > 0) {
                  sourceData[`va-actuals-${vaActualData.data[0].period}`] =
                    vaActualData.data[0].value;
                  sourceData[`current-dpid-${vaActualData.data[0].period}`] =
                    vaActualData.data[0].id;
                  sourceData[`va-actuals-dpid-${vaActualData.data[0].period}`] =
                    vaActualData.data[0].id;
                  sourceData[`unit-${vaActualData.data[0].period}`] =
                    vaActualData.data[0].unit;
                  sourceData[`precision`] = vaActualData.data[0].precision;
                  sourceData["dapparameterid"] =
                    `${vaActualData.data[0].param_id}` || null;
                } else {
                  //set default data
                  sourceData[`va-actuals-${params.period}`] = null;
                  sourceData[`current-dpid-${params.period}`] = null;
                  sourceData[`va-actuals-dpid-${params.period}`] = null;
                  sourceData[`unit-${params.period}`] = null;
                  sourceData[`precision`] = null;
                  sourceData["dapparameterid"] = null;
                }

                if (surpriseData && surpriseData.data.length > 0) {
                  sourceData[`surprise-${surpriseData.data[0].period}`] =
                    surpriseData.data[0].surprise;
                } else {
                  //set default data
                  sourceData[`surprise-${params.period}`] = null;
                }

                if (preqData && preqData.data.length > 0) {
                  sourceData[`preq-${preqData.data[0].period}`] =
                    preqData.data[0].value;
                  sourceData[`preq-dpid-${preqData.data[0].period}`] =
                    preqData.data[0].id;
                  sourceData["dapparameterid"] =
                    `${preqData.data[0].param_id}` || null;
                } else {
                  sourceData[`preq-${params.period}`] = null;
                  sourceData[`preq-dpid-${params.period}`] = null;
                  sourceData["dapparameterid"] = null;
                }
                return sourceData;
              });
              rowData.expand = true;
              return rowData;
            });

          if (this.paramFireCount === this.surpriseMeta.periods.length) {
            this.loading = false;
            this.paramFireCount = 0;
            this.listOfMapData.forEach((item) => {
              this.mapOfExpandedData[item.key] = this.convertTreeToList(item);
            });
          }
        }
      });
    }
  }

  convertTreeToList(root: TreeNodeInterface): TreeNodeInterface[] {
    const stack: TreeNodeInterface[] = [];
    const array: TreeNodeInterface[] = [];
    const hashMap = {};
    stack.push({
      ...root,
      level: 0,
      expand: root.expand ? root.expand : false,
    });

    // if filter is already applied show count of all visible lines
    if (
      Object.keys(this.gridFilters).length > 0 &&
      this.gridFilters.filterLineItem !== undefined &&
      this.gridFilters.filterLineItem.trim().length > 0
    ) {
      this.tabLineCountMapping[root.name] = root.children
        ? root.children.filter(
            (child) => child.hidden === false && child.header === undefined,
          ).length
        : "";
    } else {
      // remove dummy header from count
      this.tabLineCountMapping[root.name] = root.children
        ? root.children.filter((child) => child.header === undefined).length
        : "";
    }

    while (stack.length !== 0) {
      const node = stack.pop()!;
      this.visitNode(node, hashMap, array);
      if (node.children) {
        for (let i = node.children.length - 1; i >= 0; i--) {
          stack.push({
            ...node.children[i],
            level: node.level! + 1,
            expand: node.children[i].expand ? true : false,
            parent: node,
          });
        }
      }
    }

    return array;
  }

  visitNode(
    node: TreeNodeInterface,
    hashMap: { [key: string]: boolean },
    array: TreeNodeInterface[],
  ): void {
    if (!hashMap[node.key]) {
      hashMap[node.key] = true;
      array.push(node);
    }
  }

  getExpandedColumnHeight(item) {
    return item.isExpandableRow && item.expand
      ? this.selectedSourceList.length * 31
      : 31;
  }

  onFilterChange(param?: Array<string>) {
    this.listOfMapData.map((data) => {
      if (data.children) {
        data.children.map((rowData) => {
          rowData.hidden = false;

          // percent change filter start
          rowData["highlight"] = [];
          if (this.gridFilters.surprise === "default") {
            rowData.hidden = false;
            for (let i = 0; i < this.surpriseMeta.periods.length; i++) {
              rowData[`highlight-green-${this.surpriseMeta.periods[i]}`] =
                false;
              rowData[`highlight-red-${this.surpriseMeta.periods[i]}`] = false;
            }
          } else {
            if (rowData.header) {
              rowData.hidden = true;
            } else {
              let filterPass = 0;
              for (let i = 0; i < this.surpriseMeta.periods.length; i++) {
                let val = Number(this.gridFilters.surprise);
                val = rowData.unit === "%" ? val * 100 : val;

                let currentVal =
                  rowData[`surprise-${this.surpriseMeta.periods[i]}`];
                if (currentVal !== undefined && currentVal !== null) {
                  currentVal =
                    typeof currentVal === "string"
                      ? currentVal.replace(",", "")
                      : currentVal;
                  currentVal = Math.abs(currentVal);

                  if (currentVal >= val) {
                    filterPass++;
                  }

                  if (
                    rowData[`surprise-${this.surpriseMeta.periods[i]}`] >= val
                  ) {
                    rowData[`highlight-green-${this.surpriseMeta.periods[i]}`] =
                      true;
                    rowData[`highlight-red-${this.surpriseMeta.periods[i]}`] =
                      false;
                  } else if (
                    rowData[`surprise-${this.surpriseMeta.periods[i]}`] < -val
                  ) {
                    rowData[`highlight-red-${this.surpriseMeta.periods[i]}`] =
                      true;
                    rowData[`highlight-green-${this.surpriseMeta.periods[i]}`] =
                      false;
                  } else {
                    rowData[`highlight-green-${this.surpriseMeta.periods[i]}`] =
                      false;
                    rowData[`highlight-red-${this.surpriseMeta.periods[i]}`] =
                      false;
                  }
                }
              }

              if (filterPass > 0) {
                rowData.hidden = false;
              } else {
                rowData.hidden = true;
              }
            }
          }
          // percent change filter end

          // lineitem filter code start
          if (rowData.header) {
            rowData.hidden = true;
          }
          if (!rowData.hidden) {
            if (
              rowData.name
                .toLowerCase()
                .indexOf(this.gridFilters.filterLineItem.toLowerCase()) !== -1
            ) {
              rowData.hidden = false;
            } else {
              rowData.hidden = true;
            }
          }
          // lineitem filter code end
          rowData.expand = false;
          return rowData;
        });
      }
      return data;
    });
    this.listOfMapData.forEach((item) => {
      this.mapOfExpandedData[item.key] = this.convertTreeToList(item);
    });

    //save filters
    this.saveUserFilters();

    //Logging
    if (param) {
      if (param.indexOf("percentChange") !== -1) {
        const selected =
          this.gridFilters.surprise === "default"
            ? "All"
            : `${this.gridFilters.surprise}%`;
        const percentChangeExtras = {
          beginning_state_referrer: "Surprise Analysis Page",
          "Filter type": selected,
        };
        this.appService.logUserAction(
          {},
          this.surpriseMeta.ticker,
          "",
          "Surprise Percent Filter",
          percentChangeExtras,
          "",
          "Surprise Percent Filter Click",
        );
      }
      if (
        param.indexOf("lineitem") !== -1 &&
        this.gridFilters.filterLineItem !== ""
      ) {
        const liTextChangeExtras = {
          beginning_state_referrer: "Surprise Analysis Page",
          term_entered: this.gridFilters.filterLineItem,
        };
        this.appService.logUserAction(
          {},
          this.surpriseMeta.ticker,
          "",
          "Surprise line item filter",
          liTextChangeExtras,
          "",
          "Surprise Line item filter",
        );
      }
    }
    this.setClearStatusInRightPanel();
  }

  setDPParams(item, period, columnName) {
    this.highlightBackground = "";
    if (columnName !== "surprise") {
      const ticker = this.surpriseMeta.ticker;
      const cut =
        item.parent.view !== undefined
          ? item.parent.view
          : item.childRow !== undefined && item.childRow
            ? item.parent.parent.view
            : "";
      const source = item.source;
      const date = new Date(this.popOverMapping[`${period}-${columnName}`]);
      const dpId = item[`${columnName}-dpid-${period}`];
      const dpIdForExtras = `${dpId.split("|")[0]}|${dayjs(date).format(
        "MM/DD/YYYY",
      )}`;
      this.highlightBackground = dpId + columnName + source;
      const growthType = "Analytics";
      const extras = `${
        dpId.split("|")[0]
      }|${period.toLowerCase()}${columnName.toLowerCase()}${source}`;
      const dpParams = {
        dp: dpIdForExtras,
        cut: cut,
        ticker: ticker,
        source: columnName === "va-actuals" ? "VA Actuals" : source,
        growthType: growthType,
        shouldLog: true,
        historical_focus: "VA Actuals",
        period: period,
        param: item.dapparameterid,
        revision: columnName.toLowerCase(),
        extras: extras,
      };

      this.appService.logUserAction(
        {},
        ticker,
        cut,
        "Surprise cell click",
        {
          beginning_state_referrer: "Surprise Analysis Page",
          period: period,
          dpid: dpId,
        },
        source,
        "Surprise cell click",
      );

      this.store.dispatch(new SetDpParams(dpParams));
    }
  }

  onShowMorePeriodsClicked() {
    this.store.dispatch(new SetSourceMetaGridFilters(true));
    const firstPeriod = this.surpriseMeta.periods[0];
    const firstPeriodYear = Number(firstPeriod.split("-")[1]);
    let periodsToPrepend = [
      `FY-${firstPeriodYear - 1}`,
      `FY-${firstPeriodYear - 2}`,
      `FY-${firstPeriodYear - 3}`,
    ];

    const totalRemaining = firstPeriodYear - this.surpriseMeta.start_fiscal;
    periodsToPrepend = periodsToPrepend.slice(0, Math.min(totalRemaining, 3));
    periodsToPrepend.reverse();
    this.addPeriod(periodsToPrepend);
  }

  addPeriod(periods) {
    //add periods to surprise meta
    this.surpriseMeta.periods = [...periods, ...this.surpriseMeta.periods];
    const allPeriods = this.surpriseMeta.periods;
    this.gridFilters.period_list = [...allPeriods];
    this.gridFilters.show_clear = true;

    const colWidth = "90px";
    const colSpan = this.gridFilters.onlySurprise ? 1 : 3;

    //add column to the level1 headers
    const newHeadersL1 = [];
    periods.forEach((period) => {
      newHeadersL1.unshift({
        label: period,
        colspan: colSpan,
        width: colWidth,
        borderRight: true,
      });
    });

    this.gridHeader.level1 = [
      ...this.gridHeader.level1.slice(0, 3),
      ...newHeadersL1,
      ...this.gridHeader.level1.slice(3),
    ];

    //adjust the grid width for newly added columns
    this.bindSurpriseHeader();

    //identify expanded tabs
    const expandedTabs = this.listOfMapData.filter((tab) => tab.expand);

    this.periodFireCount =
      allPeriods.length - expandedTabs.length * periods.length;

    expandedTabs.forEach((tab) => {
      periods.forEach((period) => {
        this.loading = true;
        this.getPgData(tab.shortname, period);
      });
    });
  }

  //Trigger the popup modal for charts
  showChartPopup(rowData: any, chartType: string) {
    const includedSource = [];
    const excludedSource = [];
    this.selectedSourceList.forEach((src) => {
      this.isSourceExcluded[src]
        ? excludedSource.push(src)
        : includedSource.push(src);
    });
    this.selectedSourceList = includedSource.concat(excludedSource);
    this.chartPopupDataToUse = {
      companyFullName: this.surpriseMeta.tickerFullName,
      period: this.surpriseMeta.periods[0],
      source: rowData.source,
      sourceList: ["Consensus"]
        .concat(this.selectedSourceList)
        .map((source) => {
          return {
            label: source,
            value: source,
            checked: source === rowData.source || source === "Consensus",
            excluded: this.isSourceExcluded[source],
          };
        }),
      cut: "NMV",
      gAdjust: "implied",
      export: true,
      dapparamId: rowData.childRow
        ? rowData.parent.dapparameterid
        : rowData.dapparameterid,
      ticker: this.surpriseMeta.ticker,
      current_quarter: this.surpriseMeta.current_quarter,
      forecast: false,
    };

    this.chartPopupType = chartType;
    this.chartPopupVisible = true;
  }
  //Close the popup modal for charts
  hideChartPopup() {
    this.chartPopupVisible = false;
    this.chartPopupType = "";
  }

  onLineItemColumnCollapse() {
    this.lineItemHeadersExpanded = !this.lineItemHeadersExpanded;
    this.bindSurpriseHeader();
  }

  saveUserFilters() {
    const jsonData = { filters: { ...this.gridFilters } };
    const ticker = this.surpriseMeta.ticker;
    this.dataService.saveUserFilters(jsonData, ticker).subscribe();
  }

  /**
   * scroll to the last period after all pg calls are finished
   */
  scrollToLastPeriod() {
    const container = document.getElementsByClassName("last-td")[0];
    if (container) {
      container.scrollIntoView();
    }
  }

  setClearStatusInRightPanel() {
    // same object returns true, not same then false
    // true -> Don't set clear Flag
    // false -> set clear Flag
    const defaultGridFilters = {
      surprise: "default",
      filterLineItem: "",
      cut_expansions: ["Monitor"],
      onlySurprise: false,
      period_list: this.surpriseMeta.periods,
    };
    const diffInFilter = this.appService.compare(
      defaultGridFilters,
      this.gridFilters,
    );
    this.store.dispatch(new SetSourceMetaGridFilters(!diffInFilter));
  }
}
